import { useState } from 'react';
import { Plus } from 'lucide-react';
import { ToneDot } from '@/components/views/atoms/ToneDot';
import { cn } from '@/lib/utils';
import type { AppLabel } from '@/lib/app-collaboration';
import type { DataTableCellTone } from '@shared/types/views';

const TONES: Array<{ value: DataTableCellTone; label: string }> = [
  { value: 'good', label: 'Vert' }, { value: 'warning', label: 'Ambre' }, { value: 'critical', label: 'Rouge' }, { value: 'neutral', label: 'Gris' },
];

/** A label as a small chip. */
export const LabelChip = ({ label }: { label: AppLabel }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-domino-cardBorder bg-domino-white px-1.5 py-px text-[11px] text-domino-ink">
    <ToneDot tone={label.tone} className="h-1.5 w-1.5" />{label.name}
  </span>
);

interface AppLabelsFieldProps {
  labels: AppLabel[];
  selected: string[];
  canCreate: boolean;
  onToggle: (labelId: string, on: boolean) => Promise<void>;
  onCreate: (name: string, tone: DataTableCellTone) => Promise<void>;
}

/** The labels of an object: anyone in the app sets them; who creates them depends on the app. */
export const AppLabelsField = ({ labels, selected, canCreate, onToggle, onCreate }: AppLabelsFieldProps) => {
  const [name, setName] = useState('');
  const [tone, setTone] = useState<DataTableCellTone>('neutral');
  if (labels.length === 0 && !canCreate) return null;
  return (
    <div>
      <p className="text-xs text-domino-inkSoft">Étiquettes</p>
      <div className="mt-1 flex flex-wrap gap-1.5 empty:hidden">
        {labels.map((label) => {
          const on = selected.includes(label.id);
          return (
            <button key={label.id} type="button" aria-pressed={on} onClick={() => void onToggle(label.id, !on)}
              className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition',
                on ? 'border-domino-aubergine bg-domino-lilac text-domino-aubergine' : 'border-domino-cardBorder text-domino-inkSoft hover:border-domino-aubergine')}>
              <ToneDot tone={label.tone} />{label.name}
            </button>
          );
        })}
      </div>
      {canCreate && (
        <form className="mt-2 flex flex-wrap items-center gap-2" onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          void onCreate(name.trim(), tone).then(() => setName(''));
        }}>
          <input value={name} onChange={(event) => setName(event.target.value)} maxLength={40} placeholder="Nouvelle étiquette"
            aria-label="Nom de la nouvelle étiquette" className="w-40 rounded-lg border border-domino-cardBorder px-2 py-1 text-xs" />
          <select value={tone} onChange={(event) => setTone(event.target.value as DataTableCellTone)} aria-label="Couleur"
            className="rounded-lg border border-domino-cardBorder px-2 py-1 text-xs">
            {TONES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button type="submit" className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-domino-aubergine hover:bg-domino-muted">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />Créer
          </button>
        </form>
      )}
    </div>
  );
};
